'use strict';

import {keys} from 'ts-transformer-keys';
import _ from 'lodash';

import metaModel from '~/shared/src/utilities/metaModel';
import ReferencePropertySet from '~/shared/src/mm/ReferencePropertySet';
import Reference from '~/shared/src/ci/Reference';
import ReferenceProperty, {ReferencePropertyAttributes} from '~/shared/src/ci/ReferenceProperty';
import EnumReferenceProperty from '~/shared/src/ci/EnumReferenceProperty';
import {renderValue} from '~/shared/src/utilities/valueRenderers';
import {FilterValueType} from '~/shared/src/validationSchemes/modules/overview';

import dbDefinitionMapper from '~/backend/src/connectors/db/dbDefinitionMapper';


const allowedAttributes = keys<ReferencePropertyAttributes>() as string[];

class ReferencePropertyTemplateStringResolver {
    rawMode : boolean = false;

    protected _reference : Reference;

    constructor( reference : Reference ) {
        this._reference = reference;

        return new Proxy( this, this );
    }

    set( target : ReferencePropertyTemplateStringResolver, attribute : string, value : any ) {
        let retVal = false;

        if ( attribute === 'rawMode' ) {
            retVal = true;
            this.rawMode = value;
        }

        return retVal;
    }

    get( target : ReferencePropertyTemplateStringResolver, propertyTypeName : string ) {
        const referencePropertyTypeId = metaModel[ this._reference.schema ].getPropertyType( _.upperFirst( propertyTypeName ) )?.id ?? 0;

        let retVal : any;

        if ( referencePropertyTypeId ) {
            if ( this._reference.properties.has( referencePropertyTypeId ) ) {
                retVal = new Proxy( this._reference.properties.get( referencePropertyTypeId )!, { get: this.getByReferenceProperty.bind( this ) } );
            }
            else if ( this._reference.enumProperties.has( referencePropertyTypeId ) ) {
                retVal = new Proxy( this._reference.enumProperties.get( referencePropertyTypeId )!, { get: this.getByEnumReferenceProperty.bind( this ) } );
            }
            else {
                const referencePropertySet = metaModel[ this._reference.schema ].getReferencePropertySetForReferenceSetAndPropertyType( this._reference.referenceSetId, referencePropertyTypeId );

                if ( referencePropertySet ) {
                    retVal = new Proxy( referencePropertySet, { get: this.getByReferencePropertySet.bind( this ) } );
                }
            }
        }

        if ( !retVal ) {
            retVal = new Proxy( { name: `referenceProperty.${propertyTypeName}` }, { get: this.getByNone.bind( this ) } );
        }

        return retVal;
    }

    getByReferenceProperty( referenceProperty : ReferenceProperty, attribute : Symbol, referencePropertyProxy : ReferenceProperty ) : ( () => string );
    getByReferenceProperty( referenceProperty : ReferenceProperty, attribute : string, referencePropertyProxy : ReferenceProperty ) : string;
    getByReferenceProperty( referenceProperty : ReferenceProperty, attribute : string | Symbol, referencePropertyProxy : ReferenceProperty ) : string | ( () => string ) {
        let retVal : string | ( () => string ) = '';

        if ( typeof attribute === 'symbol' ) {
            if ( attribute === Symbol.toPrimitive ) {
                retVal = () => {
                    return referencePropertyProxy.value as string;
                };
            }
        }
        else {
            retVal = attribute as string;

            attribute = _.lowerFirst( attribute as string );

            if ( allowedAttributes.includes( attribute ) ) {
                const rawValue = referenceProperty[ attribute as keyof ReferencePropertyAttributes ];

                if ( rawValue !== undefined && rawValue !== null && rawValue !== '' ) {
                    if ( attribute === 'value' ) {
                        const referencePropertySet = referenceProperty.referencePropertySet,
                              valueType            = referencePropertySet.valueType,
                              params : any         = {
                                  valueUnit: referencePropertySet.valueUnit
                              };

                        let rawMode : boolean = this.rawMode;

                        if ( valueType === 'BOOLEAN' || valueType === 'COLOR' || valueType === 'COORDINATE' || valueType === 'EMAIL' || valueType === 'IMAGEDATA' || valueType === 'JSON' || valueType === 'LINK' || valueType === 'TEXT' ) {
                            rawMode = true;
                        }

                        retVal = renderValue( valueType, rawValue, params, rawMode ) as string;
                    }
                    else {
                        retVal = renderValue( dbDefinitionMapper.schemas[ referenceProperty.schema ].views[ 'referenceProperty' ].getFieldType( attribute ) as FilterValueType, rawValue, undefined, this.rawMode ) as string;
                    }
                }
                else {
                    retVal = '';
                }
            }
            else if ( attribute === 'valueLabel' ) {
                retVal = referenceProperty.valueLabel ?? '';
            }
            else if ( attribute === 'propertyTypeId' || attribute === 'typeId' ) {
                retVal = String( referenceProperty.referencePropertySet.propertyTypeId );
            }
            else if ( attribute === 'propertyTypeName' || attribute === 'typeName' ) {
                retVal = !this.rawMode ? referenceProperty.referencePropertySet.label : referenceProperty.referencePropertySet.name;
            }
            else if ( attribute === 'statusName' ) {
                retVal = !this.rawMode ? referenceProperty.status.label : referenceProperty.status.name;
            }
        }

        if ( !retVal ) {
            retVal = `referenceProperty.${referenceProperty.referencePropertySet.name}.${String( attribute )}`;
        }

        return retVal;
    }

    getByEnumReferenceProperty( enumReferenceProperty : EnumReferenceProperty, key : Symbol, enumReferencePropertyProxy : EnumReferenceProperty ) : ( () => string ) | undefined;
    getByEnumReferenceProperty( enumReferenceProperty : EnumReferenceProperty, key : string, enumReferencePropertyProxy : EnumReferenceProperty ) : ReferenceProperty[] | undefined;
    getByEnumReferenceProperty( enumReferenceProperty : EnumReferenceProperty, key : string, enumReferencePropertyProxy : EnumReferenceProperty ) : ReferenceProperty | undefined;
    getByEnumReferenceProperty( enumReferenceProperty : EnumReferenceProperty, key : string | Symbol, enumReferencePropertyProxy : EnumReferenceProperty ) : ReferenceProperty | ReferenceProperty[] | ( () => string ) | undefined {
        let retVal : ( () => string ) | ReferenceProperty | ReferenceProperty[] | undefined;

        if ( typeof key === 'symbol' ) {
            if ( key === Symbol.toPrimitive ) {
                retVal = () => {
                    const referenceProperties : ReferenceProperty[] = [ ...enumReferenceProperty.properties.values() ],
                          keyValuePairs : string[]                  = [];

                    referenceProperties.sort( ( referenceProperty1, referenceProperty2 ) => {
                        if ( referenceProperty1.key && referenceProperty2.key ) {
                            if ( referenceProperty1.key > referenceProperty2.key ) {
                                return 1;
                            }
                            else if ( referenceProperty1.key < referenceProperty2.key ) {
                                return -1;
                            }
                        }

                        return 0;
                    } );

                    for ( let i = 0; i < referenceProperties.length; i++ ) {
                        keyValuePairs.push( `${referenceProperties[ i ].stringKey}: ${referenceProperties[ i ].value}` );
                    }

                    return keyValuePairs.join( '; ' );
                };
            }
        }
        else if ( typeof key === 'string' ) {

            // get referenceProperty by stringKey
            if ( enumReferenceProperty.properties.has( key ) ) {
                retVal = new Proxy( enumReferenceProperty.properties.get( key )!, { get: this.getByReferenceProperty.bind( this ) } );
            }

            // get property by index
            else if ( String( Number( key ) ) === key ) {
                const referenceProperties : ReferenceProperty[] = [ ...enumReferenceProperty.properties.values() ];

                referenceProperties.sort( ( referenceProperty1, referenceProperty2 ) => {
                    if ( referenceProperty1.key && referenceProperty2.key ) {
                        if ( referenceProperty1.key > referenceProperty2.key ) {
                            return 1;
                        }
                        else if ( referenceProperty1.key < referenceProperty2.key ) {
                            return -1;
                        }
                    }

                    return 0;
                } );

                if ( referenceProperties[ Number( key ) ] ) {
                    retVal = new Proxy( referenceProperties[ Number( key ) ], { get: this.getByReferenceProperty.bind( this ) } );
                }
            }

            // get referenceProperties by key
            else {
                const referenceProperties : ReferenceProperty[] = [ ...enumReferenceProperty.properties.values() ];

                retVal = [];

                referenceProperties.sort( ( referenceProperty1, referenceProperty2 ) => {
                    if ( referenceProperty1.key && referenceProperty2.key ) {
                        if ( referenceProperty1.key > referenceProperty2.key ) {
                            return 1;
                        }
                        else if ( referenceProperty1.key < referenceProperty2.key ) {
                            return -1;
                        }
                    }

                    return 0;
                } );

                for ( let i = 0; i < referenceProperties.length; i++ ) {
                    if ( referenceProperties[ i ].key === key ) {
                        ( retVal as ReferenceProperty[] ).push( new Proxy( referenceProperties[ i ], { get: this.getByReferenceProperty.bind( this ) } ) );
                    }
                }

                if ( !retVal.length ) {
                    retVal = undefined;
                }
            }
        }

        return retVal;
    }

    getByReferencePropertySet( referencePropertySet : ReferencePropertySet, attribute : Symbol ) : ( () => string );
    getByReferencePropertySet( referencePropertySet : ReferencePropertySet, attribute : string ) : string;
    getByReferencePropertySet( referencePropertySet : ReferencePropertySet, attribute : string | Symbol ) : string | ( () => string ) {
        let retVal : string | ( () => string ) = '';

        if ( typeof attribute === 'symbol' ) {
            if ( attribute === Symbol.toPrimitive ) {
                retVal = () => {
                    return '';
                };
            }
        }
        else {
            retVal = attribute as string;

            attribute = _.lowerFirst( attribute as string );

            if ( allowedAttributes.includes( attribute ) ) {
                retVal = '';
            }
            else if ( attribute === 'propertySetId' || attribute === 'typeId' ) {
                retVal = String( referencePropertySet.propertyTypeId );
            }
            else if (
                attribute === 'referencePropertySetName' || attribute === 'setName'
                || attribute === 'propertyTypeName' || attribute === 'typeName'
            ) {
                retVal = !this.rawMode ? referencePropertySet.label : referencePropertySet.name;
            }
            else if ( attribute === 'statusName' ) {
                retVal = '';
            }
        }

        if ( !retVal ) {
            retVal = `referenceProperty.${referencePropertySet.name}.${String( attribute )}`;
        }

        return retVal;
    }

    getByNone( none : { name : string }, attribute : Symbol ) : ( () => string );
    getByNone( none : { name : string }, attribute : string ) : string;
    getByNone( none : { name : string }, attribute : string | Symbol ) : string | ( () => string ) {
        let retVal : string | ( () => string ) = '';

        if ( typeof attribute === 'symbol' ) {
            if ( attribute === Symbol.toPrimitive ) {
                retVal = () => {
                    return none.name;
                };
            }
        }
        else {
            retVal = `${none.name}.${attribute}`;
        }

        return retVal;
    }
}

export default ReferencePropertyTemplateStringResolver;
